import React from 'react';
import { ChatText, UserCircle } from '@phosphor-icons/react';
import { useAuthOptional } from '../../contexts/AuthContext';
import { useFeedbackModalOptional } from '../../contexts/FeedbackModalContext';

export type EngagementStripVariant = 'compact' | 'card';

type EngagementStripLocale = 'en' | 'fi' | 'sv';

interface EngagementStripCopy {
  authedPrompt: string;
  guestPrompt: string;
  signIn: string;
  sendFeedback: string;
  regionAria: string;
}

/**
 * Plain locale-keyed lookup, not react-intl: some callers (e.g. ResourcePageLayout) render
 * this strip in layouts without an IntlProvider mounted, so FormattedMessage/useIntl aren't
 * safe here. Mirrors the +title.ts META-lookup pattern used elsewhere for the same reason.
 */
const COPY: Record<EngagementStripLocale, EngagementStripCopy> = {
  en: {
    authedPrompt: 'Spot something wrong or have an idea? We read every message.',
    guestPrompt: 'Help us improve JobCrawls — sign in to sync saved jobs across devices, or send feedback anytime.',
    signIn: 'Sign in',
    sendFeedback: 'Send feedback',
    regionAria: 'Account and feedback',
  },
  fi: {
    authedPrompt: 'Huomasitko jotain väärää tai onko sinulla idea? Luemme jokaisen viestin.',
    guestPrompt: 'Auta meitä parantamaan JobCrawlsia — kirjaudu sisään synkronoidaksesi tallennetut työpaikat laitteiden välillä, tai lähetä palautetta milloin tahansa.',
    signIn: 'Kirjaudu sisään',
    sendFeedback: 'Lähetä palautetta',
    regionAria: 'Tili ja palaute',
  },
  sv: {
    authedPrompt: 'Upptäckte du något fel eller har en idé? Vi läser varje meddelande.',
    guestPrompt: 'Hjälp oss förbättra JobCrawls — logga in för att synka sparade jobb mellan enheter, eller skicka feedback när som helst.',
    signIn: 'Logga in',
    sendFeedback: 'Skicka feedback',
    regionAria: 'Konto och feedback',
  },
};

function resolveEngagementStripLocale(locale: string | undefined): EngagementStripLocale {
  return locale === 'fi' || locale === 'sv' ? locale : 'en';
}

export interface EngagementStripProps {
  feedbackSource: string;
  feedbackMetadata?: Record<string, unknown>;
  variant?: EngagementStripVariant;
  className?: string;
  locale?: string;
}

/**
 * Reusable sign-in + feedback strip for guests (sign-in only when logged out).
 * Feedback is offered for everyone when a feedback modal provider is present.
 *
 * Does not depend on IntlProvider — safe to render in layouts without it
 * (e.g. ResourcePageLayout). Pass `locale` for translated copy; defaults to English.
 */
export function EngagementStrip({
  feedbackSource,
  feedbackMetadata,
  variant = 'card',
  className = '',
  locale,
}: EngagementStripProps) {
  const copy = COPY[resolveEngagementStripLocale(locale)];
  const auth = useAuthOptional();
  const feedback = useFeedbackModalOptional();

  const isAuthenticated = auth?.isAuthenticated === true;
  const signIn = auth?.signIn;
  const canFeedback = feedback != null;
  const showSignIn = !isAuthenticated && Boolean(signIn);
  const showFeedback = canFeedback;

  if (!showSignIn && !showFeedback) {
    return null;
  }

  const isCompact = variant === 'compact';
  const shell = isCompact
    ? 'rounded-xl border border-[#DDD5C8]/80 dark:border-[#4A3520]/80 bg-[#F8F5F0]/90 dark:bg-[#170F07]/80 px-3 py-2.5'
    : 'rounded-2xl border border-[#DDD5C8] dark:border-[#4A3520] bg-white dark:bg-[#1F1509] p-4 sm:p-5 shadow-sm dark:shadow-none';

  return (
    <div
      className={`${shell} ${className}`}
      role="region"
      aria-label={copy.regionAria}
    >
      <div
        className={
          isCompact
            ? 'flex flex-col sm:flex-row sm:flex-wrap sm:items-center sm:justify-center gap-2 sm:gap-3 text-center'
            : 'flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between'
        }
      >
        <p
          className={
            isCompact
              ? 'text-xs text-[#5A4A3A] dark:text-[#B8A48C]'
              : 'text-sm text-[#3D3529] dark:text-[#C4A882] max-w-prose'
          }
        >
          {isAuthenticated ? copy.authedPrompt : copy.guestPrompt}
        </p>
        <div className={`flex flex-wrap items-center gap-2 ${isCompact ? 'justify-center' : 'sm:justify-end'}`}>
          {showSignIn && signIn ? (
            <button
              type="button"
              onClick={() => signIn()}
              className="inline-flex items-center justify-center gap-1.5 rounded-full bg-[#3B5E45] px-4 py-2 text-xs sm:text-sm font-semibold text-white shadow-sm transition hover:bg-[#2F4A38] focus:outline-none focus-visible:ring-2 focus-visible:ring-[#98CFA8] dark:shadow-none cursor-pointer"
            >
              <UserCircle className="h-4 w-4 shrink-0" />
              {copy.signIn}
            </button>
          ) : null}
          {showFeedback ? (
            <button
              type="button"
              onClick={() => feedback!.openFeedback({ source: feedbackSource, metadata: feedbackMetadata })}
              className="inline-flex items-center justify-center gap-1.5 rounded-full border border-[#DDD5C8] bg-white px-4 py-2 text-xs sm:text-sm font-medium text-[#3D3529] transition hover:bg-[#F2EADF] focus:outline-none focus-visible:ring-2 focus-visible:ring-[#3B5E45]/40 dark:border-[#4A3520] dark:bg-[#2A1D0F] dark:text-[#C4A882] dark:hover:bg-[#342415] dark:focus-visible:ring-[#98CFA8]/40 cursor-pointer"
            >
              <ChatText className="h-4 w-4 shrink-0" />
              {copy.sendFeedback}
            </button>
          ) : null}
        </div>
      </div>
    </div>
  );
}
